import { memo } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  highlight: {
    // Background-only highlight: padding/border-radius on an inline <mark>
    // injects horizontal space inside the inline box, which visually
    // separates the highlighted run from the next character (so searching
    // "variable" against "Variables" rendered as "Variable s" with a gap).
    // Keep the highlight flush with adjacent characters so word boundaries
    // remain readable.
    backgroundColor: tokens.colorNeutralBackground1Selected,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    padding: 0,
    borderRadius: 0,
  },
});

type HighlightTextProps = {
  text: string;
  query: string;
  className?: string;
  id?: string;
};

/**
 * Renders text with matching substrings highlighted.
 * Case-insensitive substring matching — simple and predictable.
 *
 * Memoized: each instance only re-renders when its own `text`/`query` change,
 * so a search-driven parent re-render doesn't force every connector/operation
 * label in the picker to recompute its highlight spans.
 */
export const HighlightText = memo(function HighlightText({
  text,
  query,
  className,
  id,
}: HighlightTextProps) {
  const styles = useStyles();

  if (!query || query.length < 2) {
    return <span id={id} className={className}>{text}</span>;
  }

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const queryLen = query.length;
  const parts: { text: string; highlight: boolean }[] = [];
  let lastIndex = 0;

  let matchIndex = lowerText.indexOf(lowerQuery, lastIndex);
  while (matchIndex !== -1) {
    if (matchIndex > lastIndex) {
      parts.push({ text: text.slice(lastIndex, matchIndex), highlight: false });
    }
    parts.push({ text: text.slice(matchIndex, matchIndex + queryLen), highlight: true });
    lastIndex = matchIndex + queryLen;
    matchIndex = lowerText.indexOf(lowerQuery, lastIndex);
  }

  // No matches — render plain text without splitting into spans.
  if (parts.length === 0) {
    return <span id={id} className={className}>{text}</span>;
  }

  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex), highlight: false });
  }

  return (
    <span id={id} className={className}>
      {parts.map((part, i) =>
        part.highlight ? (
          <mark key={i} className={styles.highlight}>{part.text}</mark>
        ) : (
          // Wrap non-highlight runs in a keyed <span> too. Returning bare
          // strings here makes React reconcile a mixed
          // (element-with-key | string) array — when the query changes the
          // same slot can flip between a string and an element, which
          // throws "Each child in a list should have a unique 'key' prop"
          // and crashes the picker render.
          <span key={i}>{part.text}</span>
        )
      )}
    </span>
  );
});
